<template>
  <cg-select
    v-model="modelValue"
    :items="selectedCountryStates"
    :loading="loading"
    :validation="stateValidation"
    filterable
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-for="(_, slotName) in $slots">
      <template :slot="slotName">
        <slot :name="slotName" />
      </template>
    </template>
  </cg-select>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import type { AxiosInstance } from 'axios';
import type ValidationMethod from '@corporategift/design-system/validations/ValidationMethod';
import CgSelect from '../CgSelect';

import createAxiosInstance from '../../assets/axios/createAxiosInstance';

type CgSelectInstance = InstanceType<typeof CgSelect>

interface IStateItem {
  text: string;
  value: string;
}

interface ICountriesStates {
  [key: string]: Array<IStateItem>,
}

export default Vue.extend({
  name: 'CgStateSelect',
  components: {
    CgSelect,
  },
  props: {
    /**
     * The input’s value
     * @model
     */
    value: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Api base URL - at default it'll try to take URL from .env file
     * **process.env.VUE_APP_API_URL** || **process.env.mainApiBaseUrl**
     */
    apiBaseUrl: {
      type: String,
      required: false,
      default: null,
    },
    country: {
      type: String,
      required: false,
      default: 'US',
    },
    prefetchCountries: {
      type: Array as PropType<Array<string>>,
      required: false,
      default: () => (['US', 'CA']),
    },
  },
  data: () => ({
    countriesStates: {} as ICountriesStates,
    loading: false,
    apiInstance: null as AxiosInstance | null,
  }),
  computed: {
    modelValue: {
      get (): string | null { return this.value },
      set (val: string) { this.$emit('input', val) }
    },
    selectedCountryStates (): Array<IStateItem> {
      const { country, getStatesListBasedOnCountyCode } = this
      const availableStates = getStatesListBasedOnCountyCode(country || 'US') || []
      return availableStates
    },
    stateValidation (): Array<ValidationMethod> {
      const { selectedCountryStates } = this
      const { validation }: { validation?: Array<ValidationMethod> } = this.$attrs

      if (validation && selectedCountryStates.length) { return validation }
      return []
    },
  },
  watch: {
    country: {
      immediate: false,
      handler: 'getSelectedCountryState'
    },
  },
  created () {
    const { apiBaseUrl, loading, country } = this
    
    this.apiInstance = createAxiosInstance(apiBaseUrl || undefined);

    if (loading) { this.loadStatesList([country]) }
  },
  mounted () {
    const { prefetchCountries, country, getStatesListBasedOnCountyCode } = this

    const statesList = localStorage.getItem('statesList')
    this.countriesStates = (statesList ? JSON.parse(statesList) : {})

    this.getSelectedCountryState(country)

    const result = prefetchCountries.reduce<Array<IStateItem[]>>((acc, countryCode) => {
      acc.push(getStatesListBasedOnCountyCode(countryCode))
      return acc
    }, [])

    if (result.some((states) => states === null)) {
      this.loadStatesList(this.prefetchCountries)
    }
  },
  methods: {
    getSavedStatesList (): ICountriesStates {
      const statesList = localStorage.getItem('statesList')
      return statesList ? JSON.parse(statesList) : {}
    },
    getStatesListBasedOnCountyCode (countyCode: keyof ICountriesStates): Array<IStateItem> {
      return this.countriesStates?.[countyCode] ?? null
    },
    loadStatesList (countriesCodeArray: Array<string> = []): void {
      this.loading = true
      const currentStatesList = this.getSavedStatesList()

      this.apiInstance?.get<ICountriesStates>('/api/shared/data/states', {
        params: { countries: countriesCodeArray?.join(','), },
      })
        .then(({ data }) => {
          const newCountriesList: ICountriesStates = { ...currentStatesList, ...data }

          this.countriesStates = newCountriesList || currentStatesList || {}
          localStorage.setItem('statesList', JSON.stringify(newCountriesList || currentStatesList))
        })
        .finally(() => (this.loading = false))
    },
    getSelectedCountryState (val: string | null) {
      if (val) {
        const statesList = this.getStatesListBasedOnCountyCode(val)

        if (!statesList || !statesList?.length) {
          this.loadStatesList([val])

          this.modelValue = null
        }
      }
    },
    getCgSelectInstance (): CgSelectInstance {
      return this.$children[0] as CgSelectInstance
    },
    /**
     * Validates input. Returns **true** if successful and **false** if not.
     * @public
     */
    validate (): boolean {
      const cgSelectInstance = this.getCgSelectInstance()
      return cgSelectInstance.validate()
    },
    /**
     * Resets validation.
     * @public
     */
    resetValidation (): void {
      const cgSelectInstance = this.getCgSelectInstance()
      return cgSelectInstance.resetValidation()
    },
  },
});
</script>
